import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type { IntlService } from 'ember-intl'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'

export interface CourseProgressTotalsData {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
    student_average: number
  }
  months: {
    month: string
    count: number
  }[]
}

interface Args {
  data: CourseProgressTotalsData
  grade: string
  precinct: 'lessons' | 'reading' | 'spelling' | 'my_lessons'
  product: 'maths' | 're' | 'rex'
  summaryTitle: string
  title: string
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class CourseProgressTotals extends Component<Signature> {
  @service intl!: IntlService

  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  /**
   * Chart Y axis label.
   */
  readonly yAxisLabel = 'Number of lessons'

  /**
   * List of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories(): string[] {
    const monthData = this.args.data.months ?? []
    const months = monthData.map((data) => data.month)
    return months.map((month) => convertDateFormat(month))
  }

  /**
   * Theme colours for the chart
   */
  get color() {
    const { product, precinct } = this.args
    switch (`${product}.${precinct}`) {
      case 'maths.lessons':
        return colors.forestGreen300
      case 're.spelling':
      case 'rex.spelling':
        return colors.grapeyGreen300
      case 're.reading':
      case 'rex.my_lessons':
        return colors.oceanyBlue300
      default:
        return colors.oceanyBlue300
    }
  }

  /**
   * Computed list of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get columns(): SeriesColumnOptions[] {
    const monthData = this.args.data.months ?? []
    const color = this.color
    return [{ name: 'count', data: monthData.map((data) => data.count), color, type: 'column' }]
  }

  /**
   * Computed list of summary statistics, extracted from the input data.
   * Display total lessons completed and student counts, and average lessons per student.
   */
  get summaryStats(): Stat[] {
    const summaryData = this.args.data.summary ?? []
    const total = summaryData.total_count
    const studentCount = summaryData.student_count
    const average = summaryData.student_average

    return [
      { label: this.intl.t('reporting.statBoxes.courseProgress.lessonsCompleted'), value: formatNumber(total, 1) },
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.studentCount'),
        value: formatNumber(studentCount, 1),
      },
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.perStudentCount'),
        value: formatNumber(average, 1),
      },
    ]
  }

  get summaryBoxColorScheme() {
    if (this.args.precinct === 'spelling') return SummaryBoxColorScheme.Green
    if (this.args.product === 'maths') return SummaryBoxColorScheme.DarkGreen
    return SummaryBoxColorScheme.LightBlue
  }

  <template>
    <div data-test-giraffe-chart-course-progress-totals class="relative h-full" ...attributes>
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.summaryBoxColorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @yAxisLabel={{this.yAxisLabel}}
            @chartSpacing={{this.chartSpacing}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default CourseProgressTotals
